import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { MatSnackBar } from "@angular/material/snack-bar";
import { OdontogramaService } from "app/services/odontograma.service";
import { OdontogramaConfigService } from "app/services/odontograma-config.service";
import { EstadoOdontograma, EntradaDiente } from "app/models/odontograma.interface";
import { CaraPopoverComponent } from "app/shared/odontograma/cara-popover/cara-popover.component";
import { CondicionConfig } from "app/shared/odontograma/catalogo-condiciones";

@Component({
  selector: "app-crear-odontograma",
  templateUrl: "./crear-odontograma.component.html",
  styleUrls: ["./crear-odontograma.component.scss"],
})
export class CrearOdontogramaComponent implements OnInit {
  @Input() pacienteId!: number;
  @Input() medicoId!: number;
  @Input() centroMedicoId!: number;
  @Input() fecha?: string | Date;
  @Input() observaciones?: string | null;
  @Input() fechaNacimiento?: string;
  @Input() mostrarGuardar = true;
  @Output() odontogramaCreado = new EventEmitter<number>();

  @ViewChild("caraMenuTrigger") caraMenuTrigger?: MatMenuTrigger;

  estado: EstadoOdontograma = {};
  denticion: "adulto" | "nino" | "mixta" = "adulto";
  condicionesCara: CondicionConfig[] = [];
  ultimoFecha?: string;
  guardando = false;

  menuX = 0;
  menuY = 0;
  fdiSeleccionado: string | null = null;
  caraSeleccionada: string | null = null;

  constructor(
    private service: OdontogramaService,
    private configService: OdontogramaConfigService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.configService.cargar().subscribe(() => {
      this.condicionesCara = this.configService.cara.filter((c) => !c.oculto);
    });
    this.condicionesCara = this.configService.cara.filter((c) => !c.oculto);
    this.service.ultimoPorPaciente(this.pacienteId).subscribe({
      next: (o) => (this.ultimoFecha = o.fecha),
      error: () => (this.ultimoFecha = undefined),
    });
    this.denticion = this.denticionSugerida();
  }

  private denticionSugerida(): "adulto" | "nino" | "mixta" {
    if (!this.fechaNacimiento) return "adulto";
    const nac = new Date(this.fechaNacimiento);
    if (isNaN(nac.getTime())) return "adulto";
    const hoy = new Date();
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
    if (edad < 6) return "nino";
    if (edad <= 12) return "mixta";
    return "adulto";
  }

  empezarNuevo(): void {
    this.estado = {};
  }

  precargarUltimo(): void {
    this.service.ultimoPorPaciente(this.pacienteId).subscribe({
      next: (o) => {
        this.estado = JSON.parse(JSON.stringify(o.odontograma_estado || {}));
      },
      error: () =>
        this.snackBar.open("Sin odontogramas previos para este paciente", "OK", { duration: 3000 }),
    });
  }

  onSurfaceClick(e: { fdi: string; cara: string; x: number; y: number }): void {
    this.fdiSeleccionado = e.fdi;
    this.caraSeleccionada = e.cara;
    this.menuX = e.x;
    this.menuY = e.y;
    setTimeout(() => this.caraMenuTrigger?.openMenu(), 0);
  }

  onToothLabelClick(e: { fdi: string }): void {
    this.abrirPopoverDiente(e.fdi);
  }

  abrirPopoverDiente(fdi: string | null): void {
    if (!fdi) return;
    this.fdiSeleccionado = fdi;
    const entryActual: EntradaDiente =
      this.estado[fdi] || { tooth: null, surfaces: {}, notas: null };
    const ref = this.dialog.open(CaraPopoverComponent, {
      data: { fdi, entry: entryActual },
      width: "480px",
      panelClass: "cara-popover-panel",
      autoFocus: false,
    });
    ref.afterClosed().subscribe((entryNueva: EntradaDiente | undefined) => {
      if (entryNueva === undefined) return;
      this.guardarEntry(fdi, entryNueva);
    });
  }

  setCaraCondicion(condicion: string | null): void {
    const fdi = this.fdiSeleccionado;
    const cara = this.caraSeleccionada;
    if (!fdi || !cara) return;
    const entry: EntradaDiente = this.estado[fdi]
      ? { ...this.estado[fdi], surfaces: { ...(this.estado[fdi].surfaces || {}) } }
      : { tooth: null, surfaces: {}, notas: null };
    if (condicion) {
      (entry.surfaces as any)[cara] = condicion;
    } else {
      delete (entry.surfaces as any)[cara];
    }
    this.guardarEntry(fdi, entry);
  }

  private guardarEntry(fdi: string, entry: EntradaDiente): void {
    const esVacia =
      !entry.tooth && Object.keys(entry.surfaces || {}).length === 0 && !entry.notas;
    const nuevoEstado = { ...this.estado };
    if (esVacia) {
      delete nuevoEstado[fdi];
    } else {
      nuevoEstado[fdi] = entry;
    }
    this.estado = nuevoEstado;
  }

  guardar(): void {
    if (!this.pacienteId || !this.medicoId || !this.centroMedicoId) {
      this.snackBar.open("Completá médico y centro médico", "OK", { duration: 3000 });
      return;
    }
    this.guardando = true;
    this.service
      .crear({
        idPaciente: this.pacienteId,
        medico: this.medicoId,
        centroMedico: this.centroMedicoId,
        fecha: this.fechaParaGuardar(),
        observaciones: this.observaciones ?? null,
        denticion: this.denticion,
        odontograma_estado: this.estado,
      })
      .subscribe({
        next: (r) => {
          this.guardando = false;
          this.odontogramaCreado.emit(r.id);
        },
        error: () => {
          this.guardando = false;
          this.snackBar.open("Error al guardar el odontograma", "OK", { duration: 4000 });
        },
      });
  }

  private fechaParaGuardar(): string {
    const d = this.fecha ? new Date(this.fecha) : new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
}
